import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbUpOffAlt as ThumbUpOffAltIcon
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { useUser } from "shared/helpers/axios";

const LikeButton = ({
  contentType,
  contentId,
  initialLikeCount = 0,
  initialIsLiked = false,
  size = 'large',
  showCount = true,
  onLikeChange,
  disabled = false
}) => {
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [loading, setLoading] = useState(false);
  const [likers, setLikers] = useState([]);
  const [loadingLikers, setLoadingLikers] = useState(false);

  const { user: currentUser } = useUser();


  useEffect(() => {
    setIsLiked(initialIsLiked);
    setLikeCount(initialLikeCount);
  }, [initialIsLiked, initialLikeCount]);

  // Fetch likers when component mounts or when like count changes
  useEffect(() => {
 
      fetchLikers();
 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [likeCount, contentType, contentId]);

  const fetchLikers = async () => {
    try {
      setLoadingLikers(true);
      const response = await axiosService.get('/interact/likes/', {
        params: {
          content_type: contentType,
          object_id: contentId,
          page_size: 5
        }
      });
      const likesData = response.data?.results || response.data || [];
      setLikers(likesData);
      const actualIsLiked = likesData.some(like => like?.user_info?.id === currentUser?.id); // Replace with actual current user ID check
      setIsLiked(actualIsLiked);
      setLikeCount(likesData.length); // Update like count based on actual likers

    } catch (error) {
      console.error('Error fetching likers:', error);
      setLikers([]);
    } finally {
      setLoadingLikers(false);
    }
  };

  const handleLikeToggle = async () => {
    if (loading || disabled) return;

    setLoading(true);
    const wasLiked = isLiked;

    try {
      if (wasLiked) {
        // Unlike
        await axiosService.delete('/interact/likes/unlike/', {
          params: {
            content_type: contentType,
            object_id: contentId
          }
        });
        setIsLiked(false);
        setLikeCount(prev => Math.max(0, prev - 1));
      } else {
        // Like
        await axiosService.post('/interact/likes/', {
          content_type: contentType,
          object_id: contentId
        });
        setIsLiked(true);
        setLikeCount(prev => prev + 1);
      }

      // Refresh likers list after like/unlike
      await fetchLikers();

      // Notify parent component
      if (onLikeChange) {
        onLikeChange({
          contentType,
          contentId,
          isLiked: !wasLiked,
          likeCount: wasLiked ? likeCount - 1 : likeCount + 1
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic update on error
      setIsLiked(wasLiked);
      setLikeCount(wasLiked ? likeCount : likeCount - 1);
    } finally {
      setLoading(false);
    }
  };

  // Generate rich tooltip content
  const getTooltipContent = () => {
    if (loadingLikers) {
      return <Typography variant="caption" sx={{ color: '#fff' }}>Loading...</Typography>;
    }
    if (likers.length === 0) {
      return <Typography variant="caption" sx={{ color: '#fff' }}>No likes yet</Typography>;
    }

    const shown = likers.slice(0, 5);
    const remaining = likeCount - shown.length;

    return (
      <Box sx={{ p: 0.5, minWidth: 120 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff', display: 'block', mb: 0.5 }}>
          {likeCount} {likeCount === 1 ? 'like' : 'likes'}
        </Typography>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 0.5 }} />
        <Stack spacing={0.25}>
          {shown.map((like, i) => (
            <Box key={i} display="flex" alignItems="center" gap={0.75}>
              <Avatar
                src={like.user_info?.avatar}
                alt={like.user_info?.name}
                sx={{ width: 16, height: 16, fontSize: 10 }}
              >
                {like.user_info?.name?.charAt(0)}
              </Avatar>
              <Typography variant="caption" sx={{ color: '#fff', lineHeight: 1.3 }}>
                {like.user_info?.name || 'Unknown'}
              </Typography>
            </Box>
          ))}
          {remaining > 0 && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>
              and {remaining} more
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Box display="flex" alignItems="center" gap={0}>
      <Tooltip
        title={getTooltipContent()}
        arrow
        placement="bottom"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'rgba(30,30,30,0.95)',
              borderRadius: 1.5,
              p: 1,
              boxShadow: 3,
              '& .MuiTooltip-arrow': { color: 'rgba(30,30,30,0.95)' },
            },
          },
        }}
      >
        <IconButton
          onClick={handleLikeToggle}
          disabled={loading || disabled}
          size={size}
          aria-label={isLiked ? 'Unlike' : 'Like'}
          sx={{
            color: isLiked ? 'blue' : 'text.secondary',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: 'blue',
              transform: 'scale(1.1)',
            },
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : isLiked ? (
            <ThumbUpIcon fontSize={size} />
          ) : (
            <ThumbUpOffAltIcon fontSize={size} />
          )}
        </IconButton>
      </Tooltip>

      {showCount && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: '20px', textAlign: 'left',  pl: -1 }}
        >
          {likeCount > 0 ? likeCount : ''}
        </Typography>
      )}
    </Box>
  );
};

export default LikeButton;